import type { CustomerLevel, CustomerPause, CustomerProfile } from '../api/schemas';
import { formatTime, isSameDay } from './format';

/**
 * The team's ficha on a client, in each vertical's own words. A trainer thinks
 * in levels, a dentist in conditions, a barber in the cuts a client likes; the
 * data underneath is the same level plus free tags plus notes.
 */

export const LEVELS: readonly CustomerLevel[] = ['principiante', 'intermedio', 'avanzado'];

export const LEVEL_LABEL: Record<CustomerLevel, string> = {
  principiante: 'Principiante',
  intermedio: 'Intermedio',
  avanzado: 'Avanzado',
};

const LEVEL_PLURAL: Record<CustomerLevel, [string, string]> = {
  principiante: ['principiante', 'principiantes'],
  intermedio: ['intermedio', 'intermedios'],
  avanzado: ['avanzado', 'avanzados'],
};

/** Mirrors the API: at most twelve tags, forty characters each. */
export const MAX_TAGS = 12;
export const MAX_TAG_LENGTH = 40;
export const MAX_NOTE_LENGTH = 2000;
/** Mirrors the API's ficha at a glance (migration 0078). */
export const MAX_INJURIES_LENGTH = 280;
export const MAX_MUSIC_LENGTH = 120;
export const MAX_PAUSE_REASON_LENGTH = 120;
export const MAX_PAUSE_DAYS = 366;

export type FileVocabulary = {
  /** Whether this vertical grades clients by level. */
  levels: boolean;
  /** Whether the ficha opens with injuries, music and the favorite coach (people who train). */
  glance: boolean;
  tagsLabel: string;
  tagSuggestions: readonly string[];
  notePlaceholder: string;
  /** Short hint beside the photo button, in the vertical's terms. */
  photoLabel: string;
};

const FITNESS: FileVocabulary = {
  levels: true,
  glance: true,
  tagsLabel: 'A tener en cuenta',
  tagSuggestions: [
    'Primera vez',
    'Lesión de rodilla',
    'Lesión de espalda',
    'Lesión de hombro',
    'Embarazo',
    'Postparto',
    'Necesita modificaciones',
    'Listo para subir de nivel',
  ],
  notePlaceholder: 'Ej.: Trabajó bien el core, cuidar la rodilla derecha en saltos.',
  photoLabel: 'Foto',
};

const VOCABULARY: Record<string, FileVocabulary> = {
  classstudio: FITNESS,
  gym: FITNESS,
  trainingcenter: FITNESS,
  tutor: {
    ...FITNESS,
    glance: false,
    tagSuggestions: ['Primera vez', 'Aprende con ejemplos', 'Necesita repaso', 'Prepara examen', 'Va adelantado'],
    notePlaceholder: 'Ej.: Repasamos fracciones, la próxima vez empezar con problemas.',
  },
  doctor: {
    levels: false,
    glance: false,
    tagsLabel: 'Condiciones',
    tagSuggestions: [
      'Sensibilidad',
      'Ortodoncia',
      'Implantes',
      'Bruxismo',
      'Encías',
      'Ansiedad dental',
      'Alergias',
      'Tratamiento en curso',
    ],
    notePlaceholder: 'Ej.: Sensibilidad en molares inferiores, usar anestesia tópica.',
    photoLabel: 'Foto o radiografía',
  },
  barbershop: {
    levels: false,
    glance: false,
    tagsLabel: 'Cortes que le gustan',
    tagSuggestions: ['Fade bajo', 'Fade medio', 'Fade alto', 'Taper', 'Tijera', 'Barba', 'Raya marcada', 'Cejas'],
    notePlaceholder: 'Ej.: Número 1 a los lados, tijera arriba, raya a la izquierda.',
    photoLabel: 'Foto del corte',
  },
  salon: {
    levels: false,
    glance: false,
    tagsLabel: 'Lo que le gusta',
    tagSuggestions: ['Balayage', 'Rubio', 'Keratina', 'Capas', 'Flequillo', 'Rizos', 'Cuero cabelludo sensible'],
    notePlaceholder: 'Ej.: Fórmula 7.1 con 20 vol, 35 minutos. No le gusta muy corto.',
    photoLabel: 'Foto del look',
  },
  nailstudio: {
    levels: false,
    glance: false,
    tagsLabel: 'Lo que le gusta',
    tagSuggestions: ['Acrílico', 'Gel', 'Francés', 'Almendra', 'Cuadrada', 'Uñas cortas', 'Diseños'],
    notePlaceholder: 'Ej.: Almendra corta, tonos nude. Cutícula sensible.',
    photoLabel: 'Foto del diseño',
  },
  spa: {
    levels: false,
    glance: false,
    tagsLabel: 'A tener en cuenta',
    tagSuggestions: ['Presión suave', 'Presión fuerte', 'Piel sensible', 'Embarazo', 'Alergias', 'Sin aromas'],
    notePlaceholder: 'Ej.: Prefiere presión media, tensión en hombros.',
    photoLabel: 'Foto',
  },
  restaurant: {
    levels: false,
    glance: false,
    tagsLabel: 'Preferencias',
    tagSuggestions: ['Vegetariano', 'Sin gluten', 'Alergia a mariscos', 'Terraza', 'Cumpleaños', 'Cliente frecuente'],
    notePlaceholder: 'Ej.: Le gusta la mesa junto a la ventana. Celebra aniversario en marzo.',
    photoLabel: 'Foto',
  },
};

const DEFAULT_VOCABULARY: FileVocabulary = {
  levels: false,
  glance: false,
  tagsLabel: 'Etiquetas',
  tagSuggestions: ['Cliente frecuente', 'Prefiere WhatsApp', 'Puntual', 'Suele llegar tarde'],
  notePlaceholder: 'Escribe lo que el equipo debe saber antes de atenderle.',
  photoLabel: 'Foto',
};

export function fileVocabulary(category: string | undefined): FileVocabulary {
  return (category && VOCABULARY[category]) || DEFAULT_VOCABULARY;
}

export function profileIndex(profiles: readonly CustomerProfile[]): Map<string, CustomerProfile> {
  return new Map(profiles.map((entry) => [entry.customerId, entry]));
}

export type LevelMix = Record<CustomerLevel, number> & { sinNivel: number };

/** Who is in the room by level; unknown or unlinked people count as sin nivel. */
export function levelMix(
  customerIds: readonly (string | null | undefined)[],
  index: ReadonlyMap<string, CustomerProfile>,
): LevelMix {
  return customerIds.reduce<LevelMix>(
    (mix, customerId) => {
      const level = customerId ? index.get(customerId)?.level : null;
      return level ? { ...mix, [level]: mix[level] + 1 } : { ...mix, sinNivel: mix.sinNivel + 1 };
    },
    { principiante: 0, intermedio: 0, avanzado: 0, sinNivel: 0 },
  );
}

/** "2 principiantes · 1 avanzado · 1 sin nivel", or null when nobody has a level yet. */
export function describeLevelMix(mix: LevelMix): string | null {
  const graded = LEVELS.filter((level) => mix[level] > 0);
  if (graded.length === 0) return null;
  const parts = graded.map((level) => {
    const [one, many] = LEVEL_PLURAL[level];
    return `${mix[level]} ${mix[level] === 1 ? one : many}`;
  });
  return [...parts, ...(mix.sinNivel > 0 ? [`${mix.sinNivel} sin nivel`] : [])].join(' · ');
}

function cleanTag(tag: string): string {
  return tag.trim().replace(/\s+/g, ' ').slice(0, MAX_TAG_LENGTH);
}

/** Same rules the API applies, so what the screen shows is what gets saved. */
export function normalizeTags(tags: readonly string[]): string[] {
  const seen = new Set<string>();
  return tags
    .map(cleanTag)
    .filter((tag) => {
      const key = tag.toLocaleLowerCase('es');
      if (!tag || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, MAX_TAGS);
}

export function hasTag(tags: readonly string[], tag: string): boolean {
  const key = cleanTag(tag).toLocaleLowerCase('es');
  return tags.some((entry) => entry.toLocaleLowerCase('es') === key);
}

export function toggleTag(tags: readonly string[], tag: string): string[] {
  if (hasTag(tags, tag)) {
    const key = cleanTag(tag).toLocaleLowerCase('es');
    return tags.filter((entry) => entry.toLocaleLowerCase('es') !== key);
  }
  return normalizeTags([...tags, tag]);
}

export type RosterPart = { kind: 'injuries' | 'level' | 'favorite' | 'tag'; text: string };

export type RosterPartOptions = {
  /** Whether this vertical grades clients by level. */
  graded: boolean;
  /** The roster's name for a staff id; undefined when that person is not on it. */
  staffName?: (staffId: string) => string | undefined;
  /** The coach of the class this row sits in: a favorite who is that coach goes unsaid. */
  currentStaffId?: string;
};

/**
 * What a roster row says under a name, in the order a coach mid-class needs
 * it: injuries, the level, the favorite coach when it is somebody else, then
 * two tags. Music stays in the file.
 */
export function rosterParts(entry: CustomerProfile | undefined, options: RosterPartOptions): RosterPart[] {
  if (!entry) return [];
  const favorite =
    entry.favoriteStaffId && options.currentStaffId && entry.favoriteStaffId !== options.currentStaffId
      ? options.staffName?.(entry.favoriteStaffId)
      : undefined;
  return [
    ...(entry.injuries ? [{ kind: 'injuries' as const, text: entry.injuries }] : []),
    ...(options.graded && entry.level ? [{ kind: 'level' as const, text: LEVEL_LABEL[entry.level] }] : []),
    ...(favorite ? [{ kind: 'favorite' as const, text: `Prefiere a ${favorite}` }] : []),
    ...entry.tags.slice(0, 2).map((tag) => ({ kind: 'tag' as const, text: tag })),
  ];
}

/** The one line a roster row carries under a name: injuries, level, then tags. */
export function profileSummary(entry: CustomerProfile | undefined, showLevel: boolean): string | null {
  const parts = rosterParts(entry, { graded: showLevel });
  return parts.length > 0 ? parts.map((part) => part.text).join(' · ') : null;
}

// ---------------------------------------------------------------------------
// Pauses: a client traveling is not a client gone quiet
// ---------------------------------------------------------------------------

const PANAMA_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Panama' });
const PAUSE_DAY = new Intl.DateTimeFormat('es-PA', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const PAUSE_DAY_YEAR = new Intl.DateTimeFormat('es-PA', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const DAY_MS = 24 * 60 * 60 * 1000;

/** Today on the Panama calendar, `YYYY-MM-DD`: the calendar a pause is written in. */
export function panamaToday(now: Date): string {
  return PANAMA_DAY.format(now);
}

/** Running today, starting later, or null when there is none or it already ended. Both days inclusive. */
export function pauseState(pause: CustomerPause | null, today: string): 'active' | 'upcoming' | null {
  if (!pause || pause.until < today) return null;
  return pause.from <= today ? 'active' : 'upcoming';
}

/** Everyone whose pause covers today, so a list can leave them out of Inactivos. */
export function pausedCustomerIds(index: ReadonlyMap<string, CustomerProfile>, now: Date): Set<string> {
  const today = panamaToday(now);
  return new Set(
    [...index.values()]
      .filter((entry) => pauseState(entry.pause, today) === 'active')
      .map((entry) => entry.customerId),
  );
}

/** What is wrong with a pause before it is sent, in the API's words; null when it can be saved. */
export function pauseProblem(from: string, until: string): string | null {
  if (!from) return 'Elige desde cuándo.';
  if (!until) return 'Elige hasta cuándo.';
  const days = (Date.parse(`${until}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS;
  if (Number.isNaN(days)) return 'Elige fechas válidas.';
  if (days < 0) return 'La pausa no puede terminar antes de empezar.';
  if (days > MAX_PAUSE_DAYS) return `La pausa puede durar hasta ${MAX_PAUSE_DAYS} días.`;
  return null;
}

/** "20 oct", or "6 ene 2027" when the day is not in `today`'s year. */
export function pauseDayLabel(day: string, today: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;
  return (day.slice(0, 4) === today.slice(0, 4) ? PAUSE_DAY : PAUSE_DAY_YEAR).format(date);
}

const NOTE_DAY = new Intl.DateTimeFormat('es-PA', { day: 'numeric', month: 'short' });
const NOTE_DAY_YEAR = new Intl.DateTimeFormat('es-PA', { day: 'numeric', month: 'short', year: 'numeric' });

/** "Hoy, 9:05 a. m.", "Ayer, 6:30 p. m.", then "12 sept", with the year only when it differs. */
export function noteWhen(iso: string, now: Date): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  if (isSameDay(date, now)) return `Hoy, ${formatTime(iso)}`;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (isSameDay(date, yesterday)) return `Ayer, ${formatTime(iso)}`;
  return (date.getFullYear() === now.getFullYear() ? NOTE_DAY : NOTE_DAY_YEAR).format(date);
}

export type LevelFilter = 'todos' | CustomerLevel | 'sin-nivel';

export const LEVEL_FILTERS: readonly { id: LevelFilter; label: string }[] = [
  { id: 'todos', label: 'Todos los niveles' },
  ...LEVELS.map((level) => ({ id: level, label: LEVEL_LABEL[level] })),
  { id: 'sin-nivel', label: 'Sin nivel' },
];

export function matchesLevel(entry: CustomerProfile | undefined, filter: LevelFilter): boolean {
  if (filter === 'todos') return true;
  const level = entry?.level ?? null;
  return filter === 'sin-nivel' ? level === null : level === filter;
}
